import { useState, useEffect, useCallback, useRef, type FocusEvent } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Info, Trash2 } from 'lucide-react';
import {
  formatCurrencyDecimals,
  formatDateOnly,
  formatGroupNightsLabel,
  formatPeriodLabelMonthYear,
  formatRealisedAmountInputValue,
  normaliseRealisedAmountInput,
  parseRealisedAmountInputToNumber,
} from '../../utils/formatters';
import {
  addCalendarDays,
  campaignPeriodMonthBounds,
  defaultManualLineStayDates,
  manualLineStayWindowBounds,
  normalizeCheckinDate,
  nightsBetweenStayDates,
  stayDatesWithinManualLineWindow,
} from '../../utils/revenueStayDates';
import { Stream2CopyrightFooter } from '../../components/Stream2CopyrightFooter';

interface Group {
  request_id: number;
  checkin_date: string;
  checkout_date?: string | null;
  number_of_nights?: number | null;
  company: string;
  client_request_reference?: string;
  estimated_revenue: number;
  realised_revenue: number | null;
  comment: string | null;
}

/** Row in the form: raw input string so decimals like "12." are not lost by controlled numeric state. */
type LocalGroup = Group & { realisedText: string };

function toLocalGroups(groups: Group[]): LocalGroup[] {
  return groups.map((g) => ({
    ...g,
    realisedText:
      g.realised_revenue != null && Number.isFinite(Number(g.realised_revenue))
        ? formatRealisedAmountInputValue(Number(g.realised_revenue))
        : '',
  }));
}

/** Submit / validate: must be a number ≥ 0 (use 0 when there was no revenue). */
function isValidRealisedForSubmit(realisedText: string): boolean {
  const n = parseRealisedAmountInputToNumber(realisedText);
  return n !== null && n >= 0;
}

/** True when the realised revenue input parses to exactly 0. */
function isZeroRealised(realisedText: string): boolean {
  return parseRealisedAmountInputToNumber(realisedText) === 0;
}

/** A comment is mandatory whenever the hotelier reports 0 revenue. */
function requiresCommentForZero(realisedText: string, comment: string | null): boolean {
  return isZeroRealised(realisedText) && !(comment ?? '').trim();
}

interface ManualLineFromApi {
  id: string;
  client_name: string;
  ref_client: string | null;
  checkin_date: string;
  checkout_date: string;
  number_of_nights?: number | null;
  estimated_revenue: number;
  realised_revenue: number | null;
  comment: string | null;
}

type LocalManualLine = {
  id: string;
  client_name: string;
  ref_client: string | null;
  checkin_date: string;
  checkout_date: string;
  estimated_revenue: number;
  comment: string | null;
  realisedText: string;
};

function toLocalManualLines(lines: ManualLineFromApi[]): LocalManualLine[] {
  return (lines || []).map((m) => ({
    id: m.id,
    client_name: m.client_name ?? '',
    ref_client: m.ref_client ?? null,
    checkin_date: normalizeCheckinDate(String(m.checkin_date)),
    checkout_date: normalizeCheckinDate(String(m.checkout_date)),
    estimated_revenue: Number(m.estimated_revenue) || 0,
    comment: m.comment ?? null,
    realisedText:
      m.realised_revenue != null && Number.isFinite(Number(m.realised_revenue))
        ? formatRealisedAmountInputValue(Number(m.realised_revenue))
        : '',
  }));
}

function createManualLineRow(periodLabel: string | undefined): LocalManualLine {
  const defaults = defaultManualLineStayDates(periodLabel);
  return {
    id: crypto.randomUUID(),
    client_name: '',
    ref_client: null,
    checkin_date: defaults?.checkin ?? '',
    checkout_date: defaults?.checkout ?? '',
    estimated_revenue: 0,
    comment: null,
    realisedText: '',
  };
}

function isManualRowEffectivelyEmpty(m: LocalManualLine): boolean {
  return (
    !m.client_name.trim() &&
    !m.ref_client?.trim() &&
    !m.checkin_date &&
    !m.checkout_date &&
    !m.realisedText.trim() &&
    !m.comment?.trim()
  );
}

function buildManualLinesPayload(lines: LocalManualLine[]): Array<{
  id?: string;
  client_name: string;
  ref_client?: string | null;
  checkin_date: string;
  checkout_date: string;
  estimated_revenue?: number;
  realised_revenue: number | null;
  comment?: string | null;
}> {
  return lines
    .filter((m) => m.client_name.trim() !== '')
    .map((m) => ({
      id: m.id,
      client_name: m.client_name.trim(),
      ref_client: m.ref_client?.trim() ? m.ref_client.trim() : null,
      checkin_date: m.checkin_date,
      checkout_date: m.checkout_date,
      estimated_revenue: m.estimated_revenue ?? 0,
      realised_revenue: parseRealisedAmountInputToNumber(m.realisedText),
      comment: m.comment?.trim() ? m.comment : null,
    }));
}

interface FormData {
  valid: boolean;
  campaign_id?: string;
  hotel_id?: number;
  hotel_name?: string;
  period_label?: string;
  groups: Group[];
  global_comment: string | null;
  validated?: boolean;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const edgeFunctionHeaders: HeadersInit = {
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
};

export default function HotelierForm() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [commaWarning, setCommaWarning] = useState<string | null>(null);

  const [localGroups, setLocalGroups] = useState<LocalGroup[]>([]);
  const [localGlobalComment, setLocalGlobalComment] = useState('');
  const [localManualLines, setLocalManualLines] = useState<LocalManualLine[]>([]);
  const [deletedManualLineIds, setDeletedManualLineIds] = useState<string[]>([]);

  const localGroupsRef = useRef<LocalGroup[]>([]);
  const localGlobalCommentRef = useRef('');
  const localManualLinesRef = useRef<LocalManualLine[]>([]);
  const deletedManualLineIdsRef = useRef<string[]>([]);
  /** Set synchronously after successful submit so debounced / keepalive drafts cannot overwrite DB. */
  const draftSavingDisabledRef = useRef(false);
  localGroupsRef.current = localGroups;
  localGlobalCommentRef.current = localGlobalComment;
  localManualLinesRef.current = localManualLines;
  deletedManualLineIdsRef.current = deletedManualLineIds;

  const fetchData = useCallback(async () => {
    if (!token) {
      setError('Missing token');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/validate_revenue_token?token=${encodeURIComponent(token)}`,
        { headers: edgeFunctionHeaders }
      );
      const json = await res.json();
      if (!json.valid) {
        setError(json.error || 'Invalid or expired link');
        setData(null);
        return;
      }
      if (json.validated) {
        draftSavingDisabledRef.current = true;
        navigate(`/hotel-report/thank-you?token=${token}`, { replace: true });
        return;
      }
      draftSavingDisabledRef.current = false;
      setData({
        valid: true,
        campaign_id: json.campaign_id,
        hotel_id: json.hotel_id,
        hotel_name: json.hotel_name,
        period_label: json.period_label,
        groups: json.groups || [],
        global_comment: json.global_comment || null,
      });
      setLocalGroups(toLocalGroups(json.groups || []));
      setLocalGlobalComment(json.global_comment || '');
      setLocalManualLines(toLocalManualLines(json.manual_lines || []));
      setDeletedManualLineIds([]);
    } catch (err) {
      setError('Failed to load form');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveResponses = useCallback(
    async (validate = false) => {
      if (!token || !data) return;
      if (!validate && draftSavingDisabledRef.current) return;

      if (validate) {
        draftSavingDisabledRef.current = true;
        const invalidIdx = localGroups.findIndex((g) => !isValidRealisedForSubmit(g.realisedText));
        if (invalidIdx !== -1) {
          draftSavingDisabledRef.current = false;
          setError(
            'Please enter a realised revenue amount for every group. Use 0 if there was no revenue for that stay. Amounts must be 0 or greater.',
          );
          return;
        }
        const missingZeroCommentIdx = localGroups.findIndex((g) =>
          requiresCommentForZero(g.realisedText, g.comment),
        );
        if (missingZeroCommentIdx !== -1) {
          draftSavingDisabledRef.current = false;
          setError(
            'Please add a comment for every group where the realised revenue is 0. A comment is required whenever you enter 0.',
          );
          return;
        }
        const manualToCheck = localManualLines.filter((m) => !isManualRowEffectivelyEmpty(m));
        for (const m of manualToCheck) {
          if (!m.client_name.trim()) {
            draftSavingDisabledRef.current = false;
            setError('Each extra group needs a client name.');
            return;
          }
          if (!m.checkin_date || !m.checkout_date) {
            draftSavingDisabledRef.current = false;
            setError('Each extra group needs check-in and check-out dates.');
            return;
          }
          if (nightsBetweenStayDates(m.checkin_date, m.checkout_date) == null) {
            draftSavingDisabledRef.current = false;
            setError('Check-out must be after check-in for each extra group.');
            return;
          }
          if (!stayDatesWithinManualLineWindow(m.checkin_date, m.checkout_date, data.period_label)) {
            const w = manualLineStayWindowBounds(data.period_label);
            draftSavingDisabledRef.current = false;
            setError(
              w
                ? `Check-in and check-out must be between ${w.first} and ${w.last} (reporting month ±1 month).`
                : 'Invalid stay dates for extra groups.',
            );
            return;
          }
          if (!isValidRealisedForSubmit(m.realisedText)) {
            draftSavingDisabledRef.current = false;
            setError(
              'Please enter realised revenue (0 or greater) for every extra group, or remove incomplete rows.',
            );
            return;
          }
          if (requiresCommentForZero(m.realisedText, m.comment)) {
            draftSavingDisabledRef.current = false;
            setError(
              'Please add a comment for every extra group where the realised revenue is 0.',
            );
            return;
          }
        }
      }

      const payload = {
        token,
        validate,
        responses: localGroups.map((g) => ({
          request_id: g.request_id,
          checkin_date: g.checkin_date,
          estimated_revenue: g.estimated_revenue,
          realised_revenue: parseRealisedAmountInputToNumber(g.realisedText),
          comment: g.comment,
        })),
        global_comment: localGlobalComment,
        manual_lines: buildManualLinesPayload(localManualLines),
        ...(deletedManualLineIds.length > 0
          ? { deleted_manual_line_ids: deletedManualLineIds }
          : {}),
      };

      // Do not clear `error` on draft autosave — it would hide validation messages
      // shortly after a failed "Validate & Submit" when the debounced save runs.
      if (validate) {
        setValidating(true);
        setError(null);
      }
      /** Successful validate navigates away; skip `setValidating(false)` in `finally` so the button does not flash normal before the route changes. */
      let shouldResetValidating = true;
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/save_revenue_responses`, {
          method: 'POST',
          headers: { ...edgeFunctionHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (!validate && res.status === 409) return;
          throw new Error(json.error || 'Save failed');
        }
        setDeletedManualLineIds([]);
        if (validate) {
          shouldResetValidating = false;
          draftSavingDisabledRef.current = true;
          navigate(`/hotel-report/thank-you?token=${token}`, { replace: true });
        }
      } catch (err) {
        // Only surface errors in the form banner when the user clicked Validate & Submit.
        // Draft autosaves can fail on incomplete manual lines; that should not alarm the user.
        if (validate) {
          draftSavingDisabledRef.current = false;
          setError(err instanceof Error ? err.message : 'Save failed');
        } else {
          console.warn('Revenue form draft save failed:', err);
        }
      } finally {
        if (shouldResetValidating) {
          setValidating(false);
        }
      }
    },
    [token, data, localGroups, localManualLines, deletedManualLineIds, localGlobalComment, navigate]
  );

  const saveDraftRef = useRef(saveResponses);
  saveDraftRef.current = saveResponses;

  const commitGroupRealisedBlur = useCallback((idx: number) => {
    setLocalGroups((prev) => {
      const next = [...prev];
      const row = next[idx];
      if (!row) return prev;
      const n = parseRealisedAmountInputToNumber(row.realisedText);
      const newText = n !== null ? formatRealisedAmountInputValue(n) : '';
      if (newText === row.realisedText) return prev;
      next[idx] = { ...row, realisedText: newText };
      localGroupsRef.current = next;
      return next;
    });
    queueMicrotask(() => {
      void saveDraftRef.current(false);
    });
  }, []);

  const commitManualRealisedBlur = useCallback((idx: number) => {
    setLocalManualLines((prev) => {
      const next = [...prev];
      const row = next[idx];
      if (!row) return prev;
      const n = parseRealisedAmountInputToNumber(row.realisedText);
      const newText = n !== null ? formatRealisedAmountInputValue(n) : '';
      if (newText === row.realisedText) return prev;
      next[idx] = { ...row, realisedText: newText };
      localManualLinesRef.current = next;
      return next;
    });
    queueMicrotask(() => {
      void saveDraftRef.current(false);
    });
  }, []);

  const flushKeepaliveDraft = useCallback(() => {
    if (!token || !data?.valid || draftSavingDisabledRef.current) return;
    const dels = deletedManualLineIdsRef.current;
    const payload = {
      token,
      validate: false,
      responses: localGroupsRef.current.map((g) => ({
        request_id: g.request_id,
        checkin_date: g.checkin_date,
        estimated_revenue: g.estimated_revenue,
        realised_revenue: parseRealisedAmountInputToNumber(g.realisedText),
        comment: g.comment,
      })),
      global_comment: localGlobalCommentRef.current,
      manual_lines: buildManualLinesPayload(localManualLinesRef.current),
      ...(dels.length > 0 ? { deleted_manual_line_ids: dels } : {}),
    };
    void fetch(`${SUPABASE_URL}/functions/v1/save_revenue_responses`, {
      method: 'POST',
      headers: { ...edgeFunctionHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  }, [token, data?.valid]);

  useEffect(() => {
    if (!token || !data?.valid || draftSavingDisabledRef.current) return;
    const timer = setTimeout(() => {
      if (draftSavingDisabledRef.current) return;
      saveDraftRef.current(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [localGroups, localManualLines, deletedManualLineIds, localGlobalComment, token, data?.valid]);

  useEffect(() => {
    if (!token || !data?.valid || draftSavingDisabledRef.current) return;
    const flush = () => {
      if (document.visibilityState === 'hidden') {
        flushKeepaliveDraft();
      }
    };
    const onPageHide = () => {
      flushKeepaliveDraft();
    };
    document.addEventListener('visibilitychange', flush);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', flush);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [token, data?.valid, flushKeepaliveDraft]);

  const flushDraftSave = useCallback(() => {
    void saveResponses(false);
  }, [saveResponses]);

  const handleRealisedChange = (idx: number, value: string) => {
    if (value.includes(',')) {
      setCommaWarning('Please use "." as decimal separator, not ","');
    } else {
      setCommaWarning(null);
    }
    const normalized = normaliseRealisedAmountInput(value);
    setLocalGroups((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], realisedText: normalized };
      return next;
    });
  };

  const handleCommentChange = (idx: number, value: string) => {
    setLocalGroups((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], comment: value };
      return next;
    });
  };

  const handleManualRealisedChange = (idx: number, value: string) => {
    if (value.includes(',')) {
      setCommaWarning('Please use "." as decimal separator, not ","');
    } else {
      setCommaWarning(null);
    }
    const normalized = normaliseRealisedAmountInput(value);
    setLocalManualLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], realisedText: normalized };
      return next;
    });
  };

  const handleRemoveManualLine = (idx: number) => {
    const row = localManualLines[idx];
    if (row?.id) {
      setDeletedManualLineIds((d) => (d.includes(row.id) ? d : [...d, row.id]));
    }
    setLocalManualLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const openDatePickerAfterSeeding = (el: HTMLInputElement) => {
    queueMicrotask(() => {
      try {
        el.showPicker?.();
      } catch {
        /* Some browsers restrict showPicker; native focus still works */
      }
    });
  };

  const handleManualCheckinFocus = (idx: number) => (e: FocusEvent<HTMLInputElement>) => {
    const bounds = campaignPeriodMonthBounds(data?.period_label);
    if (!bounds) return;
    const el = e.currentTarget;
    const row = localManualLinesRef.current[idx];
    if (row?.checkin_date) {
      openDatePickerAfterSeeding(el);
      return;
    }
    flushSync(() => {
      setLocalManualLines((prev) => {
        const next = [...prev];
        if (next[idx].checkin_date) return prev;
        next[idx] = { ...next[idx], checkin_date: bounds.first };
        return next;
      });
    });
    openDatePickerAfterSeeding(el);
  };

  const handleManualCheckoutFocus = (idx: number) => (e: FocusEvent<HTMLInputElement>) => {
    const bounds = campaignPeriodMonthBounds(data?.period_label);
    if (!bounds) return;
    const el = e.currentTarget;
    const row = localManualLinesRef.current[idx];
    if (row?.checkout_date) {
      openDatePickerAfterSeeding(el);
      return;
    }
    const checkin = row?.checkin_date;
    const defaultCheckout = checkin ? addCalendarDays(checkin, 1) : addCalendarDays(bounds.first, 1);
    flushSync(() => {
      setLocalManualLines((prev) => {
        const next = [...prev];
        if (next[idx].checkout_date) return prev;
        next[idx] = { ...next[idx], checkout_date: defaultCheckout };
        return next;
      });
    });
    openDatePickerAfterSeeding(el);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col px-4">
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="rounded-2xl bg-white px-10 py-8 shadow-sm ring-1 ring-slate-200/80 flex flex-col items-center gap-5">
            <img src="/images/stream2_logo.png" alt="Stream2" className="h-10 w-auto" />
            <div className="text-sm text-slate-500">Loading…</div>
          </div>
        </div>
        <Stream2CopyrightFooter />
      </div>
    );
  }

  if (error && !data?.valid) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col p-4">
        <div className="flex flex-1 flex-col items-center justify-center gap-6">
          <img src="/images/stream2_logo.png" alt="Stream2" className="h-10 w-auto opacity-90" />
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-8 max-w-md text-center w-full">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" aria-hidden />
            <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-2">Invalid or Expired Link</h2>
            <p className="text-sm text-slate-600 leading-relaxed">{error}</p>
            <p className="text-sm text-slate-500 mt-4 leading-relaxed">
              This link may have expired or already been used. Please contact support if you need a new link.
            </p>
          </div>
        </div>
        <Stream2CopyrightFooter />
      </div>
    );
  }

  if (!data) return null;

  const groupsPeriodTitle = `Groups in ${formatPeriodLabelMonthYear(data.period_label) ?? 'this period'}`;
  const manualStayWindow = manualLineStayWindowBounds(data.period_label);
  const addExtraGroupsButton = (
    <button
      type="button"
      onClick={() => setLocalManualLines((prev) => [...prev, createManualLineRow(data.period_label)])}
      className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-800 shadow-sm hover:bg-slate-50"
    >
      Add extra groups
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col px-4 sm:px-6">
      <div className="max-w-4xl mx-auto w-full flex-1 space-y-6 py-6 sm:py-10">
        {/* Header */}
        <header className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
          <div className="flex flex-col items-center px-6 pt-6 pb-5 border-b border-slate-100">
            <img src="/images/stream2_logo.png" alt="Stream2" className="h-10 w-auto" />
          </div>
          <div className="px-6 py-5">
            <h1 className="page-title text-balance mb-3">
              Revenue Collection – {data.hotel_name}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Reporting period</span>
              <span className="inline-flex items-center rounded-md bg-action-surface px-2.5 py-1 text-sm font-medium text-action">
                {data.period_label}
              </span>
            </div>
          </div>
        </header>

        {commaWarning && (
          <div
            aria-live="polite"
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden />
            <span className="leading-relaxed">{commaWarning}</span>
          </div>
        )}

        {/* Instructions */}
        <section
          aria-labelledby="hotelier-instructions-heading"
          className="rounded-xl border border-slate-200/90 bg-white shadow-sm px-4 py-4 sm:px-5 sm:py-4"
        >
          <div className="flex gap-3 items-start">
            <div className="min-w-0 flex-1 pt-0.5">
              <h2 id="hotelier-instructions-heading" className="text-sm font-semibold text-slate-900 mb-2">
                How to complete this form
              </h2>
              <ul className="text-sm text-slate-600 space-y-2 leading-relaxed list-disc pl-4 marker:text-slate-400">
                <li>
                  For each group, enter the realised revenue in euros. Use &quot;.&quot; as the decimal separator.
                </li>
                <li>
                  If there was no revenue for a group, enter{' '}
                  <strong className="font-semibold text-slate-800">0</strong>. Every row needs a number that is{' '}
                  <strong className="font-semibold text-slate-800">0 or greater</strong> before you can validate and
                  submit.
                </li>
                <li>
                  Whenever you enter <strong className="font-semibold text-slate-800">0</strong>, a{' '}
                  <strong className="font-semibold text-slate-800">comment is required</strong> to explain why
                  (e.g. cancellation, no-show, postponed).
                </li>
                <li>Your data is saved automatically as you type. Click "Validate & Submit" to finish.</li>
              </ul>
            </div>
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-action-surface text-action">
              <Info className="h-5 w-5" strokeWidth={2} aria-hidden />
            </div>
          </div>
        </section>

        {/* Groups table */}
        <section aria-labelledby="groups-table-heading">
          <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <h2 id="groups-table-heading" className="text-sm font-semibold text-slate-900">
              {groupsPeriodTitle}
            </h2>
            <p className="text-xs text-slate-500 sm:text-right">
              All amounts are in euros.
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 shadow-sm flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden />
              <span className="leading-relaxed">{error}</span>
            </div>
          )}

          <div className="rounded-xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
            <div className="min-w-0 max-w-full">
              <table className="w-full max-w-full table-fixed border-collapse text-[11px] leading-snug sm:text-xs">
                <colgroup>
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '18%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="border-b border-slate-200 bg-slate-50/95 align-middle px-2 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 sm:px-2.5 sm:text-[0.65rem]"
                    >
                      Client
                    </th>
                    <th
                      scope="col"
                      className="border-b border-slate-200 bg-slate-50/95 align-middle px-2 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 sm:px-2.5 sm:text-[0.65rem]"
                    >
                      Ref. client
                    </th>
                    <th
                      scope="col"
                      className="border-b border-slate-200 bg-slate-50/95 align-middle px-2 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 whitespace-nowrap sm:px-2.5 sm:text-[0.65rem]"
                    >
                      Check-in
                    </th>
                    <th
                      scope="col"
                      className="border-b border-slate-200 bg-slate-50/95 align-middle px-2 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 whitespace-nowrap sm:px-2.5 sm:text-[0.65rem]"
                    >
                      Check-out
                    </th>
                    <th
                      scope="col"
                      className="border-b border-slate-200 bg-slate-50/95 align-middle px-2 py-2 text-center text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 tabular-nums whitespace-nowrap sm:px-2.5 sm:text-[0.65rem]"
                    >
                      Nights
                    </th>
                    <th
                      scope="col"
                      className="border-b border-slate-200 bg-slate-50/95 align-middle px-2 py-2 text-right text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 tabular-nums whitespace-nowrap sm:px-2.5 sm:text-[0.65rem]"
                    >
                      Est. revenue
                    </th>
                    <th
                      scope="col"
                      className="border-b border-slate-200 bg-slate-50/95 align-middle px-2 py-2 text-right text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 tabular-nums whitespace-nowrap sm:px-2.5 sm:text-[0.65rem]"
                    >
                      <span className="sr-only">Required. </span>
                      <span className="block w-full text-right">Realised (€)</span>
                    </th>
                    <th
                      scope="col"
                      className="border-b border-slate-200 bg-slate-50/95 align-middle px-2 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 sm:px-2.5 sm:text-[0.65rem]"
                    >
                      <span className="block w-full">Comment</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-800">
                  {localGroups.map((g, idx) => {
                    const refClientFull = g.client_request_reference?.trim() ?? '';
                    const commentRequired = requiresCommentForZero(g.realisedText, g.comment);
                    return (
                    <tr
                      key={`${g.request_id}-${g.checkin_date}`}
                      className="transition-colors hover:bg-slate-50/90"
                    >
                      <td className="min-w-0 align-middle px-2 py-2 sm:px-2.5">
                        <span className="line-clamp-2 break-words">{g.company || '—'}</span>
                      </td>
                      <td
                        className="min-w-0 align-middle px-2 py-2 sm:px-2.5"
                        title={refClientFull || undefined}
                      >
                        <span className="line-clamp-2 break-words">{refClientFull || '—'}</span>
                      </td>
                      <td className="align-middle px-2 py-2 sm:px-2.5">
                        <span className="inline-block min-w-[6.5rem] whitespace-nowrap tabular-nums">
                          {formatDateOnly(g.checkin_date)}
                        </span>
                      </td>
                      <td className="align-middle px-2 py-2 sm:px-2.5">
                        <span className="inline-block min-w-[6.5rem] whitespace-nowrap tabular-nums">
                          {g.checkout_date ? formatDateOnly(g.checkout_date) : '—'}
                        </span>
                      </td>
                      <td className="align-middle px-2 py-2 text-center tabular-nums sm:px-2.5">
                        <span className="inline-block min-w-[1.5rem] whitespace-nowrap">
                          {formatGroupNightsLabel(g.number_of_nights, g.checkin_date, g.checkout_date)}
                        </span>
                      </td>
                      <td className="align-middle px-2 py-2 text-right tabular-nums whitespace-nowrap sm:px-2.5">
                        {formatCurrencyDecimals(g.estimated_revenue)}
                      </td>
                      <td className="align-middle px-2 py-2 sm:px-2.5">
                        <div className="flex min-h-8 items-center justify-end">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={g.realisedText}
                            onChange={(e) => handleRealisedChange(idx, e.target.value)}
                            onBlur={() => commitGroupRealisedBlur(idx)}
                            className="box-border h-8 w-full max-w-[5.5rem] rounded border border-slate-300 bg-white px-1.5 text-right text-[11px] tabular-nums leading-none text-slate-900 shadow-sm placeholder:text-slate-400 sm:h-8 sm:max-w-[6rem] sm:text-xs"
                          />
                        </div>
                      </td>
                      <td className="min-w-0 align-middle px-2 py-2 sm:px-2.5">
                        <div className="flex min-h-8 items-center">
                          <input
                            type="text"
                            value={g.comment || ''}
                            onChange={(e) => handleCommentChange(idx, e.target.value)}
                            onBlur={flushDraftSave}
                            placeholder={commentRequired ? 'Required when 0' : 'Optional'}
                            aria-required={commentRequired || undefined}
                            aria-invalid={commentRequired || undefined}
                            className={`box-border h-8 w-full min-w-0 rounded border bg-white px-1.5 text-left text-[11px] leading-none text-slate-900 shadow-sm sm:text-xs ${
                              commentRequired
                                ? 'border-red-400 ring-1 ring-red-300 placeholder:text-red-500'
                                : 'border-slate-300 placeholder:text-slate-400'
                            }`}
                          />
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {localManualLines.length === 0 ? (
            <div className="mt-4 flex justify-end">{addExtraGroupsButton}</div>
          ) : null}

          {localManualLines.length > 0 && (
            <div className="mt-3 rounded-xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
              <div className="min-w-0 max-w-full">
                <table className="w-full table-fixed border-collapse text-[11px] leading-snug sm:text-xs">
                  <colgroup>
                    <col style={{ width: '17%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '6%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="border-b border-slate-200 bg-slate-50/95 px-1 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 sm:px-2 sm:text-[0.65rem]">
                        Client
                      </th>
                      <th className="border-b border-slate-200 bg-slate-50/95 px-1 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 sm:px-2 sm:text-[0.65rem]">
                        Ref. client
                      </th>
                      <th className="border-b border-slate-200 bg-slate-50/95 px-1 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 whitespace-nowrap sm:px-2 sm:text-[0.65rem]">
                        Check-in
                      </th>
                      <th className="border-b border-slate-200 bg-slate-50/95 px-1 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 whitespace-nowrap sm:px-2 sm:text-[0.65rem]">
                        Check-out
                      </th>
                      <th className="border-b border-slate-200 bg-slate-50/95 px-1 py-2 text-center text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 tabular-nums sm:px-2 sm:text-[0.65rem]">
                        Nights
                      </th>
                      <th className="border-b border-slate-200 bg-slate-50/95 px-1 py-2 text-right text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 sm:px-2 sm:text-[0.65rem]">
                        Realised (€)
                      </th>
                      <th className="border-b border-slate-200 bg-slate-50/95 px-1 py-2 text-left text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 sm:px-2 sm:text-[0.65rem]">
                        Comment
                      </th>
                      <th className="border-b border-slate-200 bg-slate-50/95 px-0.5 py-2 text-center text-[0.62rem] font-semibold uppercase tracking-wide text-slate-600 sm:px-1 sm:text-[0.65rem]">
                        {' '}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-800">
                    {localManualLines.map((m, idx) => {
                      const derivedNights = nightsBetweenStayDates(m.checkin_date, m.checkout_date);
                      const manualCommentRequired = requiresCommentForZero(m.realisedText, m.comment);
                      return (
                        <tr key={m.id} className="transition-colors hover:bg-slate-50/90">
                          <td className="min-w-0 px-1 py-2 align-middle sm:px-2">
                            <input
                              type="text"
                              value={m.client_name}
                              onChange={(e) =>
                                setLocalManualLines((prev) => {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], client_name: e.target.value };
                                  return next;
                                })
                              }
                              onBlur={flushDraftSave}
                              className="box-border h-8 w-full min-w-0 rounded border border-slate-300 bg-white px-1 text-[11px] text-slate-900 shadow-sm sm:px-1.5 sm:text-xs"
                              aria-label="Client name"
                            />
                          </td>
                          <td className="min-w-0 px-1 py-2 align-middle sm:px-2">
                            <input
                              type="text"
                              value={m.ref_client ?? ''}
                              onChange={(e) =>
                                setLocalManualLines((prev) => {
                                  const next = [...prev];
                                  next[idx] = {
                                    ...next[idx],
                                    ref_client: e.target.value.trim() ? e.target.value : null,
                                  };
                                  return next;
                                })
                              }
                              onBlur={flushDraftSave}
                              className="box-border h-8 w-full min-w-0 rounded border border-slate-300 bg-white px-1 text-[11px] text-slate-900 shadow-sm sm:px-1.5 sm:text-xs"
                              aria-label="Reference client"
                            />
                          </td>
                          <td className="min-w-0 px-1 py-2 align-middle sm:px-2">
                            <input
                              type="date"
                              value={m.checkin_date}
                              min={manualStayWindow?.first}
                              max={manualStayWindow?.last}
                              onChange={(e) =>
                                setLocalManualLines((prev) => {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], checkin_date: e.target.value };
                                  return next;
                                })
                              }
                              onFocus={handleManualCheckinFocus(idx)}
                              onBlur={flushDraftSave}
                              className="box-border h-8 w-full min-w-0 max-w-full rounded border border-slate-300 bg-white px-0.5 text-[11px] text-slate-900 shadow-sm sm:px-1 sm:text-xs"
                              aria-label="Check-in date"
                            />
                          </td>
                          <td className="min-w-0 px-1 py-2 align-middle sm:px-2">
                            <input
                              type="date"
                              value={m.checkout_date}
                              min={m.checkin_date ? addCalendarDays(m.checkin_date, 1) : manualStayWindow?.first}
                              max={manualStayWindow?.last}
                              onChange={(e) =>
                                setLocalManualLines((prev) => {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], checkout_date: e.target.value };
                                  return next;
                                })
                              }
                              onFocus={handleManualCheckoutFocus(idx)}
                              onBlur={flushDraftSave}
                              className="box-border h-8 w-full min-w-0 max-w-full rounded border border-slate-300 bg-white px-0.5 text-[11px] text-slate-900 shadow-sm sm:px-1 sm:text-xs"
                              aria-label="Check-out date"
                            />
                          </td>
                          <td className="px-1 py-2 text-center align-middle tabular-nums sm:px-2">
                            {derivedNights != null ? derivedNights : '—'}
                          </td>
                          <td className="min-w-0 px-1 py-2 align-middle sm:px-2">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={m.realisedText}
                              onChange={(e) => handleManualRealisedChange(idx, e.target.value)}
                              onBlur={() => commitManualRealisedBlur(idx)}
                              placeholder="0"
                              className="box-border h-8 w-full min-w-0 rounded border border-slate-300 bg-white px-1 text-right text-[11px] tabular-nums text-slate-900 shadow-sm sm:px-1.5 sm:text-xs"
                              aria-label="Realised revenue for extra group"
                            />
                          </td>
                          <td className="min-w-0 px-1 py-2 align-middle sm:px-2">
                            <input
                              type="text"
                              value={m.comment ?? ''}
                              onChange={(e) =>
                                setLocalManualLines((prev) => {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], comment: e.target.value || null };
                                  return next;
                                })
                              }
                              onBlur={flushDraftSave}
                              placeholder={manualCommentRequired ? 'Required when 0' : 'Optional'}
                              aria-required={manualCommentRequired || undefined}
                              aria-invalid={manualCommentRequired || undefined}
                              className={`box-border h-8 w-full min-w-0 rounded border bg-white px-1 text-[11px] text-slate-900 shadow-sm sm:px-1.5 sm:text-xs ${
                                manualCommentRequired
                                  ? 'border-red-400 ring-1 ring-red-300 placeholder:text-red-500'
                                  : 'border-slate-300 placeholder:text-slate-400'
                              }`}
                            />
                          </td>
                          <td className="px-0.5 py-2 text-center align-middle sm:px-1">
                            <button
                              type="button"
                              onClick={() => handleRemoveManualLine(idx)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 text-red-700 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30"
                              aria-label="Remove this extra group"
                              title="Remove line"
                            >
                              <Trash2 className="h-4 w-4" strokeWidth={2} aria-hidden />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
                {manualStayWindow
                  ? `Check-in and check-out may be any day from ${manualStayWindow.first} through ${manualStayWindow.last} (month before, reporting month ${data.period_label}, or month after). New rows default to the first day of ${data.period_label}. Data is saved automatically.`
                  : 'Data is saved automatically.'}
              </p>
            </div>
          )}

          {localManualLines.length > 0 ? (
            <div className="mt-3 flex justify-end">{addExtraGroupsButton}</div>
          ) : null}
        </section>

        {/* Hotel comment */}
        <section
          aria-labelledby="global-comment-label"
          className="rounded-xl border border-slate-200/80 bg-white shadow-sm p-5 sm:p-6"
        >
          <label id="global-comment-label" htmlFor="global-comment" className="block text-sm font-semibold text-slate-900 mb-2">
            Hotel comment <span className="font-normal text-slate-500">(optional)</span>
          </label>
          <textarea
            id="global-comment"
            value={localGlobalComment}
            onChange={(e) => setLocalGlobalComment(e.target.value)}
            onBlur={flushDraftSave}
            rows={3}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 resize-y min-h-[5.5rem]"
            placeholder="Any additional notes for Stream2…"
          />
        </section>

        {/* Footer: submit */}
        <footer className="border-t border-slate-200 pt-6 pb-2">
          <div className="flex w-full justify-end">
            <button
              type="button"
              onClick={() => saveResponses(true)}
              disabled={validating}
              className="btn-primary w-full sm:w-auto justify-center min-h-11 px-6"
            >
              {validating ? 'Validating...' : 'Validate & Submit'}
            </button>
          </div>
        </footer>
      </div>
      <Stream2CopyrightFooter />
    </div>
  );
}
